import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { QueryClient } from '@tanstack/react-query';

let queryClient: QueryClient | null = null;
export const setQueryClient = (client: QueryClient) => {
  queryClient = client;
};

export const client = axios.create({
  withCredentials: true,
  baseURL: '/api',
});

const UNAUTHORIZED_STATUS = 401;
const AWS_AUTHORIZER_UNAUTHORIZED_STATUS = 401;
const AWS_AUTHORIZER_UNAUTHORIZED_MESSAGE = 'Unauthorized';
const MAX_RETRIES_REFRESH_TOKEN = 2;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const refreshAccessToken = async () => {
  for (let attempt = 0; attempt < MAX_RETRIES_REFRESH_TOKEN; attempt++) {
    try {
      if (attempt !== 0) {
        await wait(1000);
      }
      return await client.post('/auth/refresh');
    } catch (error) {
      if (error instanceof AxiosError) {
        const code = error.response?.status;

        if (code && code === UNAUTHORIZED_STATUS) {
          throw error;
        }

        if (attempt === MAX_RETRIES_REFRESH_TOKEN - 1) {
          throw error;
        }
      }
    }
  }
};

const logoutUser = async () => {
  await client.post('/auth/logout');
};

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
  config: AxiosRequestConfig;
}

// List to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;

    if (
      error?.response?.status === AWS_AUTHORIZER_UNAUTHORIZED_STATUS &&
      error?.response?.data?.message?.startsWith(AWS_AUTHORIZER_UNAUTHORIZED_MESSAGE)
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Refresh the access token
          await refreshAccessToken();

          queryClient?.invalidateQueries({ queryKey: ['auth', 'session'] });

          // Retry all requests in the queue with the new token
          for (const { config, resolve, reject } of refreshAndRetryQueue) {
            client
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          }

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return client(originalRequest);
        } catch (refreshError) {
          // Handle token refresh error
          useAuthStore.setState({
            refreshToken: { expiresAt: 0 },
            isAuthenticated: false,
          });

          // Clear stale session data immediately
          queryClient?.resetQueries({ queryKey: ['auth', 'session'] });

          await logoutUser();

          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({
          config: originalRequest,
          resolve,
          reject,
        } as RetryQueueItem);
      });
    }

    // Return a Promise rejection if the status code is not 403
    return Promise.reject(error);
  }
);
